/**
 * Appending an exchange rate from a screen (ADR-0061, M10-2).
 *
 * A rate is a **rational of two positive integers in major units** — USD→LBP at 89,500 is
 * `89500` over `1` — carried as strings, because a JSON number is a double and a rate that
 * drifts is I2's defect moved from the amount to the rate. The kernel's `fxSnapshot` states the
 * same unit; this is the wire form of it.
 *
 * **Only the currencies this platform prices.** The loader accepts any code, and its suite uses
 * ISO 4217's testing codes precisely so it cannot collide with a real row. A screen has no such
 * need, and a rate for a currency nothing prices is a row nothing reads — so the route is
 * narrower, and says so rather than sharing a looser rule.
 *
 * **Instants are sent as typed**, never through a `Date` on the way: a browser's parser applies
 * the viewer's time zone to a string without a zone, and a rate effective at the wrong hour is a
 * quote priced against the wrong rate for that hour.
 *
 * There is no review date: `fx_rates` has none. The owner is the rate's `source_owner`, required
 * for §7's reason — a rate somebody vouches for, by name.
 */
import { z } from 'zod';
import { renderedText } from './rendered-text.js';
export const FX_CURRENCIES = ['USD', 'LBP', 'SYP'];
/** Up to 18 digits: well inside BIGINT, and no leading zero, so `07` is not a rate. */
const positiveInteger = z.string().regex(/^[1-9]\d{0,17}$/u, 'positive_integer');
const instant = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/u, 'iso_instant');
export const appendFxRateSchema = z
    .object({
    base: z.enum(FX_CURRENCIES),
    quote: z.enum(FX_CURRENCIES),
    numerator: positiveInteger,
    denominator: positiveInteger,
    effectiveFrom: instant,
    capturedAt: instant,
    sourceId: z.string().trim().min(1).max(100),
    owner: z.string().trim().min(1).max(200),
    note: z.string().trim().min(1).max(500),
})
    .strict()
    .refine((value) => value.base !== value.quote, { path: ['quote'], message: 'same_currency' });
const POSITIVE_INTEGER_EXEMPT = 'digits with no leading zero (positive_integer), which hold no phone-shaped run';
const INSTANT_EXEMPT = 'an ISO instant ending in Z (iso_instant), which holds no phone-shaped digit run';
export const appendFxRateRendered = renderedText(appendFxRateSchema, {
    prose: ['owner'],
    refs: ['sourceId'],
    exempt: {
        numerator: POSITIVE_INTEGER_EXEMPT,
        denominator: POSITIVE_INTEGER_EXEMPT,
        effectiveFrom: INSTANT_EXEMPT,
        capturedAt: INSTANT_EXEMPT,
        note: 'stored only in the config.fx_rates.amended event payload; no view renders it',
    },
});
//# sourceMappingURL=fx.js.map